import {
  cardsProfessionals,
  roledescriptions,
} from "../../constant/Home/Dashboard";
import { Toaster } from "react-hot-toast";
import PastJobs from "../PastJobs/Pastjobs";
import BodyHeader from "../common/BodyHeader";
import ActiveJobs from "../ActiveJobs/ActiveJobs";
import PlusIcon from "../../assets/icons/plus.svg";
import chimeSound from "../../assets/sounds/properteasy-chime.mp3";

import OpenInvoices from "../OpenInvoices/Invoices";
import PaidInvoices from "../PaidInvoices/PaidInvoices";
import { UserContext } from "../../context/UserContext";
import Properties from "../common/properties/Properties";
import AvailableJobs from "../AvailableJobs/AvailableJobs";
import React, { useState, useEffect, useContext, useRef } from "react";
import { useStatisticsData } from "../../context/StatisticsData";
import { useNotifications } from "../../context/NotificationContext";
import PendingApprovals from "../PendingApprovals/PendingApprovals";
import CircularLoader from "../../Ui/Loader";
import Skeleton from "../Skeleton";
import { Messages as MessagesApi } from "../../../service.js";
import { Invoices } from "../../../service.js";
import useApiHandler from "../../hooks/ApiHandler";
import Messages from "../common/Messages/Messages.jsx";
import PaymentMethodModal from "../common/PaymentMethodModal";
import AddPropertyForm from "../common/properties/AddProperty";
import { useLocation, useNavigate } from "react-router-dom";

const Dashboard = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("activeJobs");
  const [messages, setMessages] = useState([]);
  const [newMessages, setNewMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const { userData } = useContext(UserContext);
  const { statisticsData, loading, fetchStatistics } = useStatisticsData();
  const { unreadCount, setThreadUnreadCounts, setUnreadCount } =
    useNotifications();
  const { callApi } = useApiHandler();
  const navigate = useNavigate();

  // Count from the same endpoint used by the Paid Invoices page
  const [paidInvoicesCount, setPaidInvoicesCount] = useState(null);

  const prevUnreadCounts = useRef({});
  const isInitialLoad = useRef(true);

  const playChime = () => {
    try {
      const audio = new Audio(chimeSound);
      audio.play().catch((e) => {
        // Many browsers block audio play if there hasn't been a user interaction yet
        console.warn("Chime playback blocked or failed:", e);
      });
    } catch (error) {
      console.error("Failed to initialize chime sound:", error);
    }
  };

  // Supported dashboard views (must match keys in activeComponent)
  const VIEW_KEYS = [
    "properties",
    "activeJobs",
    "pastJobs",
    "openInvoices",
    "availableJobs",
    "paidInvoices",
    "pendingApprovals",
    "messages",
    "unreadMessages",
  ];

  // Sync activeTab from URL query (?view=...) so top nav routes into dashboard correctly
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      const view = params.get("view");
      if (view && VIEW_KEYS.includes(view) && view !== activeTab) {
        setActiveTab(view);
      }
    } catch (_) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // New: show payment method modal when needed
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  useEffect(() => {
    const accountType = userData?.accountType;
    const needsPayment = userData && accountType &&
      ["LANDLORD", "PROPERTY_MANAGER", "HOMEOWNER", "STR_HOST", "FAMILY_MEMBER"].includes(accountType) &&
      userData?.hasPaymentMethod !== true;
    if (needsPayment) {
      setShowPaymentModal(true);
    } else {
      setShowPaymentModal(false);
    }
  }, [userData?.accountType, userData?.hasPaymentMethod]);

  // Modal to add a property when none exists
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [addPropertyId, setAddPropertyId] = useState(null);

  const handlePostJob = () => {
    const total = statisticsData?.totalProperties;
    if (typeof total === "number" && total === 0) {
      // Open Add Property modal flow instead of navigating
      setShowAddPropertyModal(true);
    } else {
      // Keep existing flow
      navigate("/post-job");
    }
  }

  // Fetch messages from API
  const fetchMessages = async (opts = {}) => {
    const { silent } = opts;
    try {
      if (!silent) setMessagesLoading(true);

      await callApi(
        () => MessagesApi.getMessagesList(1, 50, ""),
        {
          silent,
          onSuccess: (data) => {
            const threads = data.data?.results || [];

            // Map the API response to the expected format
            const messagesWithDetails = threads.map((thread) => {
              return {
                id: thread.id,
                threadId: thread.id,
                name: thread.last_message?.sender_name || "Unknown",
                service: `Request #${thread.request}`,
                lastMessage: thread.last_message?.body || "No messages yet",
                timestamp: thread.last_message_at || thread.created_at,
                isOnline: true, // You can determine this based on your logic
                hasOffer: false, // You can determine this based on your logic
                hasJob: false, // You can determine this based on your logic
                rating: 5, // Default rating
                profileImage: "/path/to/default/avatar.png", // You can add profile images later
                unreadCount: thread.unread_count || 0,
                isRead: thread.unread_count === 0,
                // Additional properties from API
                request: thread.request,
                participants: thread.participants,
                created_at: thread.created_at,
                last_message_at: thread.last_message_at,
                last_message: thread.last_message,
                issuetype: thread.issuetype,
              };
            });

            setMessages(messagesWithDetails);
            // Filter new messages (threads with unread messages)
            setNewMessages(
              messagesWithDetails.filter((msg) => msg.unreadCount > 0)
            );

            // Sync unread counts with NotificationContext
            const threadUnreadCounts = {};
            let totalUnreadCount = 0;

            messagesWithDetails.forEach((message) => {
              if (message.unreadCount > 0) {
                threadUnreadCounts[message.threadId] = message.unreadCount;
                totalUnreadCount += message.unreadCount;
              }
            });

            setThreadUnreadCounts(threadUnreadCounts);
            setUnreadCount(totalUnreadCount);

            // Chime logic: play if any thread has a NEW message (count increased)
            if (!isInitialLoad.current) {
              let hasNewMessage = false;
              Object.keys(threadUnreadCounts).forEach((threadId) => {
                const oldCount = prevUnreadCounts.current[threadId] || 0;
                if (threadUnreadCounts[threadId] > oldCount) {
                  hasNewMessage = true;
                }
              });

              if (hasNewMessage) {
                playChime();
              }
            } else {
              isInitialLoad.current = false;
            }

            prevUnreadCounts.current = threadUnreadCounts;
          },
          onError: (error) => {
            console.error("Failed to fetch message threads:", error);
            // Keep existing messages during background polling failures
            if (!silent) {
              setMessages([]);
              setNewMessages([]);
            }
          },
        }
      );
    } catch (error) {
      console.error("Error fetching messages:", error);
      if (!silent) {
        setMessages([]);
        setNewMessages([]);
      }
    } finally {
      if (!silent) setMessagesLoading(false);
    }
  };

  // Fetch paid invoices count using the same API as the Paid Invoices page
  const fetchPaidInvoicesCount = async (opts = {}) => {
    const { silent } = opts;
    try {
      await callApi(() => Invoices.getPaidInvoices(1, 10), {
        silent,
        onSuccess: (response) => {
          // Prefer paginated count; fallback to results length or array length
          const count =
            response?.data?.count ??
            (Array.isArray(response?.data?.results)
              ? response.data.results.length
              : Array.isArray(response?.data)
              ? response.data.length
              : 0);
          setPaidInvoicesCount(Number(count) || 0);
        },
        onError: () => {
          if (!silent) setPaidInvoicesCount(0);
        },
      });
    } catch (_) {
      if (!silent) setPaidInvoicesCount(0);
    }
  };

  // Refresh threads on-demand (e.g., websocket layer can dispatch `messages:refresh`)
  useEffect(() => {
    const onRefresh = () => {
      fetchMessages({ silent: true });
    };
    window.addEventListener("messages:refresh", onRefresh);
    return () => window.removeEventListener("messages:refresh", onRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 10s polling: keep the dashboard "alive" by re-fetching the same existing data.
  // Uses silent/background mode so cards/tabs don't flicker or spam toasts.
  useEffect(() => {
    const accountType = userData?.accountType;
    const isBlockedVendor =
      accountType === "VENDOR" && userData?.onboardingConfigured === false;

    if (!accountType || isBlockedVendor) return;

    // Initial (non-silent) load
    fetchStatistics();
    fetchMessages();
    if (accountType === "VENDOR") {
      fetchPaidInvoicesCount();
    }

    const intervalId = setInterval(() => {
      fetchStatistics({ silent: true });
      fetchMessages({ silent: true });
      if (accountType === "VENDOR") {
        fetchPaidInvoicesCount({ silent: true });
      }

      // Let the currently-open dashboard tab refresh its own list (active jobs, invoices, etc.)
      window.dispatchEvent(new Event("dashboard:poll"));
    }, 30000); // Changed from 10000ms (10s) to 30000ms (30s) to reduce API load

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.accountType, userData?.onboardingConfigured]);

  // Override statistics with the API-derived paid invoices count
  const computedStatistics = React.useMemo(() => {
    const base = statisticsData || {};
    if (typeof paidInvoicesCount === "number" && paidInvoicesCount !== null) {
      return {
        ...base,
        paidInvoices: paidInvoicesCount,
        paid_invoices: paidInvoicesCount,
        paidInvoicesCount: paidInvoicesCount,
        paid_invoices_count: paidInvoicesCount,
        invoiceCounts: {
          ...(base?.invoiceCounts || {}),
          paid: paidInvoicesCount,
        },
        invoice_counts: {
          ...(base?.invoice_counts || {}),
          paid: paidInvoicesCount,
        },
        invoices: {
          ...(base?.invoices || {}),
          paid: paidInvoicesCount,
        },
      };
    }
    return base;
  }, [statisticsData, paidInvoicesCount]);
  const activeComponent = {
    properties: (
      <Properties
        description={roledescriptions[userData.accountType]?.description}
      />
    ),
    activeJobs: <ActiveJobs />,
    pastJobs: <PastJobs />,
    openInvoices: <OpenInvoices />,
    availableJobs: <AvailableJobs />,
    paidInvoices: <PaidInvoices />,
    pendingApprovals: <PendingApprovals />,
    messages: (
      // Messages view should show the full thread list
      <Messages
        title="Messages"
        header={false}
        messages={messages}
        loading={messagesLoading}
        userData={userData}
      />
    ),
    unreadMessages: (
      <Messages
        title="Unread Messages"
        header={false}
        messages={newMessages}
        loading={messagesLoading}
        userData={userData}
      />
    ),
  };

  return (
    <>
      <Toaster />
      {/* Payment Method Prompt */}
      <PaymentMethodModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onAdd={() => setShowPaymentModal(false)}
      />
      {/* Add Property Modal (shown when user has no properties) */}
      {showAddPropertyModal && (
        <AddPropertyForm
          setModalState={() => {
            // Close the modal on any state change (we're not chaining into other modals here)
            setShowAddPropertyModal(false);
            setAddPropertyId(null);
          }}
          onSuccess={() => {
            // Close modal after successful add; statistics refresh is handled within AddPropertyForm
            setShowAddPropertyModal(false);
            setAddPropertyId(null);
          }}
          setPropertyId={setAddPropertyId}
          propertyId={addPropertyId}
        />
      )}
      {Object.keys(userData).length > 0 &&
      userData.accountType === "VENDOR" &&
      userData.onboardingConfigured === false ? (
        <Skeleton />
      ) : Object.keys(userData).length > 0 ? (
        <div className="max-w-[1440px] m-auto pt-20  ">
          <div className="flex flex-col md:gap-12 gap-6 md:px-6 px-4 md:py-12 py-6 w-full">
            <BodyHeader
              title="Welcome back!"
              description={
                roledescriptions[userData.accountType]?.welcomeMessage[
                  activeTab
                ]
              }
            >
              <div className="flex gap-2">
                {userData?.accountType !== "VENDOR" && (
                  <button
                    onClick={handlePostJob}
                    className="bg-[#3cab5c] md:h-12 mt-0 text-white md:px-4 px-2 md:py-3 py-2 rounded-md flex items-center gap-2"
                  >
                    <img src={PlusIcon} alt="plus" className="w-4 h-4" />
                    Post a job
                  </button>
                )}
              </div>
            </BodyHeader>
            <div className="flex md:gap-4 gap-2 flex-wrap justify-center items-stretch w-full cursor-pointer">
              {cardsProfessionals({
                statistics: computedStatistics,
                unreadCount: unreadCount,
                userData: userData,
              })[userData.accountType]?.map((card, index) => (
                <div
                  key={index}
                  onClick={() => {
                    if (card.value === "paymentMethods") {
                      navigate("/payment-methods");
                    } else {
                      setActiveTab(card.value);
                    }
                  }}
                  className={`${
                    activeTab === card.value ? "bg-[#52C27133]" : "bg-[#F6F7F9]"
                  } relative gap-3 flex-1 min-w-[160px] max-w-[260px] w-full py-4 px-4 rounded-[20px] flex items-start hover:shadow-sm transition-all cursor-pointer`}
                >
                  {card.isDefault && (
                    <span className="absolute top-3 right-3 bg-[#3cab5c] text-white text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                      Default
                    </span>
                  )}
                  <img
                    src={card.icon}
                    alt={card.title}
                    className="w-6 h-6 mt-1"
                  />
                  <div>
                    {loading ? (
                      <CircularLoader size="1.5rem" />
                    ) : (
                      <h1 className="text-[#242424] text-2xl md:text-3xl font-medium leading-tight">
                        {card.number}
                      </h1>
                    )}
                    <h4 className="text-[#666] text-sm font-medium">
                      {card.title}
                    </h4>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:px-6 px-4  w-full">
            {" "}
            {activeComponent[activeTab]}
          </div>
          {activeTab !== "messages" && activeTab !== "unreadMessages" && (
            <div className="md:px-6 px-4 pt-4 pb-12 w-full">
              {/* Secondary messages panel should not appear on the Messages tab */}
              <Messages
                header={false}
                title="All Messages"
                messages={messages}
                loading={messagesLoading}
                userData={userData}
              />
            </div>
          )}
        </div>
      ) : (
        <Skeleton />
      )}
    </>
  );
};

export default Dashboard;
